import { useCallback, useEffect, useState } from 'react';
import { Truck, Users, Plus, Wallet } from '../lib/icons';
import { toast } from 'sonner';
import {
  QUALITES,
  fetchTiers,
  fetchCuves,
  fetchRelevesClients,
  fetchAchatsHuile,
  fetchCompteTiers,
  enregistrerAchatHuileExterne,
  enregistrerAchatHuileClient,
  nomTiers,
} from '../lib/dataProvider';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { PaiementTiersDialog } from '../components/reception/PaiementTiersDialog';
import { ReferenceMarche } from '../components/bourse/ReferenceMarche';
import { ActionsAdmin } from '../components/shared/ActionsAdmin';
import { ContactCombobox } from '../components/shared/ContactCombobox';
import { useAuth } from '../lib/auth';
import { estAdmin } from '../lib/permissions';
import { estBlocageFiliere } from '../lib/dataProvider';
import { adminModifierAchatHuile, adminSupprimerAchatHuile } from '../lib/dataProvider';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

export default function AchatsHuile() {
  const { user } = useAuth();
  const [onglet, setOnglet] = useState('externe');
  const [tiers, setTiers] = useState([]);
  const [cuves, setCuves] = useState([]);
  const [releves, setReleves] = useState([]);
  const [achats, setAchats] = useState([]);
  const [compte, setCompte] = useState(null);
  const [paiement, setPaiement] = useState(null);
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    date: aujourdhui,
    tiersId: '',
    qualite: 'extra_vierge',
    kg: '',
    prixKg: '',
    cuveId: '',
    destination: 'laisser',
    cuveDestId: '',
    compensation: true,
  });

  const load = useCallback(() => {
    fetchCuves().then(setCuves);
    fetchRelevesClients().then(setReleves);
    fetchAchatsHuile().then(setAchats);
  }, []);

  useEffect(() => {
    load();
    fetchTiers().then(setTiers);
  }, [load]);

  useEffect(() => {
    if (f.tiersId) fetchCompteTiers(f.tiersId).then(setCompte);
    else setCompte(null);
  }, [f.tiersId, achats]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const externe = onglet === 'externe';
  const stockClient = releves.find((r) => r.nom === (f.tiersId ? nomTiers(f.tiersId) : ''))?.solde ?? 0;
  const montant = (Number(f.kg) || 0) * (Number(f.prixKg) || 0);
  const dette = compte && compte.solde < 0 ? -compte.solde : 0;
  const compense = !externe && f.compensation ? Math.min(dette, montant) : 0;

  const submit = async (e, override = false) => {
    e?.preventDefault?.();
    const action = externe
      ? enregistrerAchatHuileExterne({ ...f, cuveId: f.cuveId, override, utilisateur: user?.name })
      : enregistrerAchatHuileClient({ ...f, cuveSourceId: f.cuveId });
    const { error, row, compense: c, avertissement } = await action;
    if (error) {
      if (!override && estBlocageFiliere(error) && estAdmin(user?.role) && window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)) return submit(null, true);
      return toast.error(error);
    }
    toast.success(
      `Achat ${row.id} enregistré — ${fmt(row.kg, 3)} kg pour ${fmt(row.montant, 3)} TND${c > 0 ? ` (compensation ${fmt(c, 3)} TND)` : ''}`
    );
    if (avertissement) toast.warning(avertissement, { duration: 8000 });
    setF({ ...f, kg: '', prixKg: '' });
    load();
  };

  const totalKg = achats.reduce((s, a) => s + a.kg, 0);
  const totalMontant = achats.reduce((s, a) => s + a.montant, 0);
  const totalCompense = achats.reduce((s, a) => s + (a.compense || 0), 0);

  return (
    <div data-testid="achats-huile-page" className="space-y-6">
      <PageTitle
        emoji="🛢️"
        titre="Achats d'Huile"
        sous="Négoce externe (camion / fournisseur) et rachat de l'huile déjà stockée par vos clients"
      />

      <ReferenceMarche />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <StatBlock testid="stat-achats-kg" titre="Huile achetée" accent="border-l-slate-900" items={[['Opérations', fmt(achats.length)], ['Volume (Kg)', fmt(totalKg, 3)]]} />
        <StatBlock testid="stat-achats-montant" titre="Montant des achats" accent="border-l-amber-600" items={[['Total (TND)', fmt(totalMontant, 3)]]} />
        <StatBlock testid="stat-achats-compense" titre="Dettes compensées" accent="border-l-emerald-600" items={[['Total (TND)', fmt(totalCompense, 3)]]} />
      </div>

      <div className="flex flex-wrap gap-px bg-slate-200">
        {[
          ['externe', 'Achat Externe', 'Fournisseur extérieur / camion', Truck],
          ['client', 'Achat Huile Client (Interne)', 'Rachat d’huile stockée chez vous', Users],
        ].map(([id, titre, hint, Icone]) => (
          <button
            key={id}
            type="button"
            data-testid={`onglet-achat-${id}`}
            onClick={() => setOnglet(id)}
            className={`px-4 py-3 text-left transition-colors duration-150 ${onglet === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            <span className="flex items-center gap-2 text-sm font-bold">
              <Icone size={15} /> {titre}
            </span>
            <span className={`text-xs ${onglet === id ? 'text-slate-300' : 'text-slate-500'}`}>{hint}</span>
          </button>
        ))}
      </div>

      <form onSubmit={submit} data-testid="form-achat-huile" className="space-y-5 border border-slate-200 bg-white px-4 py-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className={label}>Date</label>
            <input data-testid="achat-date" type="date" value={f.date} onChange={set('date')} className={inputCls} />
          </div>
          <div>
            <label className={label}>{externe ? 'Fournisseur' : 'Client'}</label>
            <ContactCombobox
                contacts={tiers}
                value={f.tiersId}
                onChange={(id) => setF((p) => ({ ...p, tiersId: id }))}
                testid="achat-tiers"
              />
          </div>
          {externe && (
            <div>
              <label className={label}>Qualité de l'huile</label>
              <select data-testid="achat-qualite" value={f.qualite} onChange={set('qualite')} className={inputCls}>
                {QUALITES.map((q) => (
                  <option key={q.id} value={q.id}>{q.label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={label}>Quantité (Kg)</label>
            <input data-testid="achat-kg" type="number" step="0.001" min="0" value={f.kg} onChange={set('kg')} className={inputCls} />
            {!externe && (
              <p data-testid="stock-huile-client" className="mt-1.5 font-data text-[11px] text-slate-600">
                Stock d'huile disponible : {fmt(stockClient, 3)} Kg
              </p>
            )}
          </div>
          <div>
            <label className={label}>Prix unitaire (TND / Kg)</label>
            <input data-testid="achat-prix" type="number" step="0.001" min="0" value={f.prixKg} onChange={set('prixKg')} className={inputCls} />
          </div>
          <div>
            <label className={label}>{externe ? 'Cuve de destination' : 'Cuve contenant l’huile du client'}</label>
            <select data-testid="achat-cuve" value={f.cuveId} onChange={set('cuveId')} className={inputCls}>
              <option value="">— Choisir —</option>
              {cuves
                .filter((c) => !c.virtuelle && !c.verrouillee && c.id !== 'CUVE_SORTIE_DIRECTE')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom} · {c.famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · {fmt(c.volume)} {c.unite}                  </option>
                ))}
            </select>
          </div>
        </div>

        {!externe && (
          <div data-testid="achat-destination" className="space-y-3 border border-slate-200 p-3">
            <p className={label}>Logique de transfert</p>
            <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
              {[
                ['laisser', 'Laisser dans la cuve actuelle', 'Changement de propriété uniquement'],
                ['transferer', 'Transférer dans une autre cuve', 'Propriété + déplacement physique'],
              ].map(([id, titre, hint]) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`destination-${id}`}
                  onClick={() => setF((p) => ({ ...p, destination: id }))}
                  className={`px-3 py-3 text-left transition-colors duration-150 ${f.destination === id ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
                >
                  <p className="text-sm font-bold">{titre}</p>
                  <p className={`text-xs ${f.destination === id ? 'text-slate-300' : 'text-slate-500'}`}>{hint}</p>
                </button>
              ))}
            </div>
            {f.destination === 'transferer' && (
              <div>
                <label className={label}>Nouvelle cuve de destination</label>
                <select data-testid="achat-cuve-dest" value={f.cuveDestId} onChange={set('cuveDestId')} className={inputCls}>
                  <option value="">— Choisir —</option>
                  {cuves.filter((c) => c.id !== f.cuveId).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nom} · {c.famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · reste {fmt(c.capacite - c.volume)} {c.unite}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div data-testid="achat-compensation" className="border-l-2 border-slate-900 bg-slate-50 px-3 py-3">
              <p className="font-data text-xs text-slate-700">
                Dette actuelle du client : <span className="font-bold">{fmt(dette, 3)} TND</span> · montant de l'achat :{' '}
                <span className="font-bold">{fmt(montant, 3)} TND</span>
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                {[
                  [true, 'Compensation automatique', 'On éponge la dette puis on paie la différence'],
                  [false, 'Règlement séparé', 'Achat payé en totalité, dettes inchangées'],
                ].map(([val, titre, hint]) => (
                  <button
                    key={String(val)}
                    type="button"
                    data-testid={`compensation-${val ? 'auto' : 'separe'}`}
                    onClick={() => setF((p) => ({ ...p, compensation: val }))}
                    className={`flex-1 border px-3 py-2 text-left transition-colors duration-150 ${f.compensation === val ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white hover:bg-slate-100'}`}
                  >
                    <p className="text-sm font-bold">{titre}</p>
                    <p className={`text-xs ${f.compensation === val ? 'text-slate-300' : 'text-slate-500'}`}>{hint}</p>
                  </button>
                ))}
              </div>
              {compense > 0 ? (
                <p data-testid="apercu-compensation" className="mt-2 font-data text-xs font-semibold text-emerald-800">
                  Compensation prévue : {fmt(compense, 3)} TND · reste à payer {fmt(montant - compense, 3)} TND
                </p>
              ) : (
                <p data-testid="apercu-compensation-neutre" className="mt-2 font-data text-xs text-slate-500">
                  {dette > 0 ? 'Règlement séparé : dettes existantes inchangées.' : 'Aucune dette en cours — la totalité sera payée au client.'}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button type="submit" data-testid="achat-submit" className="flex h-12 items-center gap-2 bg-slate-900 px-6 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700">
            <Plus size={16} /> Enregistrer l'achat
          </button>
          <button
            type="button"
            data-testid="achat-regler"
            disabled={!f.tiersId}
            onClick={() => setPaiement(f.tiersId)}
            className="flex h-12 items-center gap-2 border-2 border-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400"
          >
            <Wallet size={16} /> Régler maintenant
          </button>
        </div>
      </form>

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
          Historique des achats d'huile
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-achats-huile">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Réf.', 'Date', 'Type', 'Tiers', 'Qualité', 'Kg', 'Prix (TND/Kg)', 'Montant', 'Cuve', 'Compensation', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {achats.length === 0 ? (
                <tr data-testid="achats-empty">
                  <td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-500">Aucun achat d'huile enregistré.</td>
                </tr>
              ) : (
                achats.map((a) => (
                  <tr key={a.id} data-testid={`achat-row-${a.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{a.id}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{a.date}</td>
                    <td className="px-3 py-2 font-data text-xs uppercase text-slate-600">{a.type === 'externe' ? 'externe' : 'client'}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">{nomTiers(a.tiersId)}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{QUALITES.find((q) => q.id === a.qualite)?.label ?? a.qualite}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(a.kg, 3)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(a.prixKg, 3)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(a.montant, 3)}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-600">{a.cuveNom}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-600">
                      {a.compense > 0 ? `${fmt(a.compense, 3)} TND épongés` : a.type === 'client' ? 'Règlement séparé' : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ActionsAdmin
                        id={a.id}
                        row={a}
                        titre="Corriger l’achat d’huile"
                        champs={[
                          { key: 'date', label: 'Date', type: 'date' },
                          { key: 'kg', label: 'Quantité (Kg)', type: 'number' },
                          { key: 'prixKg', label: 'Prix (TND/Kg)', type: 'number' },
                          { key: 'notes', label: 'Notes', large: true },
                        ]}
                        onModifier={(patch) => adminModifierAchatHuile(a.id, patch, user?.name)}
                        onSupprimer={() => adminSupprimerAchatHuile(a.id, user?.name)}
                        onDone={load}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {paiement && <PaiementTiersDialog tiersId={paiement} onClose={() => setPaiement(null)} onDone={load} />}
    </div>
  );
}
